/**
 * O paciente do cliente no sistema da clínica (#1024): o vínculo entre o
 * contato do CliqCRM e o paciente no SISAC, a busca e o cadastro.
 *
 * O CliqCRM guarda só o vínculo (o código do paciente). Nome, nascimento e CPF
 * são lidos do SISAC na hora, pelas partes da agenda da organização (ADR-0028),
 * e nunca copiados para o contato: o SISAC manda no paciente, o CliqCRM no
 * contato. Só nas organizações com a funcionalidade "clinica" ligada.
 */

import { z } from 'zod';

/** CPF só com os dígitos. */
const CpfSchema = z.string().regex(/^\d{11}$/, 'CPF com 11 dígitos.');

/** O código do paciente no SISAC. Não é dado de pessoa sozinho. */
const CodigoDoPacienteSchema = z.string().trim().min(1).max(60);

/**
 * A situação de uma consulta no histórico. A operação da clínica traduz a sigla
 * dela (no SISAC, o `ConfAtd`: N, C, S, F) para estas palavras; o que ela não
 * traduz chega como `outra`.
 */
export const SituacaoDaConsultaNoSisacSchema = z.enum([
  'agendada',
  'confirmada',
  'atendida',
  'falta',
  'outra',
]);
export type SituacaoDaConsultaNoSisac = z.infer<typeof SituacaoDaConsultaNoSisacSchema>;

/** O paciente como o SISAC o tem agora. O CPF vem só nos dois últimos dígitos. */
const PacienteNoSisacSchema = z.object({
  codigo: CodigoDoPacienteSchema,
  nome: z.string().nullable(),
  /** `AAAA-MM-DD`. */
  nascimento: z.string().nullable(),
  cpf_final: z.string().nullable(),
});

const ConsultaNoSisacSchema = z.object({
  /** Instante ISO 8601, como o SISAC o guarda (sem fuso). */
  data_hora: z.string().nullable(),
  situacao: SituacaoDaConsultaNoSisacSchema,
  medico: z.string().nullable(),
  /** Dado de saúde: revela a condição do paciente. */
  especialidade: z.string().nullable(),
});

export const PacienteDoClienteSchema = z.object({
  /** Nulo enquanto o contato não está vinculado a um paciente. */
  vinculo: z
    .object({
      codigo: CodigoDoPacienteSchema,
      vinculado_em: z.string().datetime(),
    })
    .nullable(),
  /** Os dados atuais do vinculado no SISAC; nulo sem vínculo ou com o SISAC fora. */
  paciente: PacienteNoSisacSchema.nullable(),
  /** As últimas consultas do vinculado, da mais nova à mais antiga (até 5). Vazia sem `pode.ver_consultas`. */
  consultas: z.array(ConsultaNoSisacSchema),
  /**
   * `fora`: o SISAC não respondeu, e só o vínculo veio. `parcial`: a leitura do
   * paciente veio, mas as consultas não.
   */
  sisac: z.enum(['ok', 'parcial', 'fora']),
  /** O que a agenda da organização permite fazer daqui. */
  pode: z.object({
    buscar_por_telefone: z.boolean(),
    /**
     * A busca por nome, sobrenome ou código: com a parte `buscar_paciente_por_termo`
     * ligada e a marca do prontuário de quem pergunta.
     */
    buscar_por_termo: z.boolean(),
    cadastrar: z.boolean(),
    /**
     * As consultas (médico e especialidade revelam a condição do paciente) só
     * vêm para quem tem a marca do prontuário (ADR-0022), até a decisão da §21.
     */
    ver_consultas: z.boolean(),
    /**
     * As "Últimas vezes" (os atendimentos no SISAC, com os procedimentos): com a
     * marca do prontuário e a parte `ultimas_vezes_paciente` ligada.
     */
    ver_ultimas_vezes: z.boolean(),
  }),
});
export type PacienteDoCliente = z.infer<typeof PacienteDoClienteSchema>;

/**
 * Um atendimento do paciente no SISAC (o "Movimento"), como a tela "Últimas
 * vezes" de lá mostra. Os textos vêm como a clínica os escreve ("Fechada",
 * "Exame"): o CRM não traduz o que não decide.
 *
 * Sem a observação (texto livre da clínica) e sem a matrícula do convênio
 * (decisão de 25/10): só o que a recepção precisa para agendar de novo.
 */
const VezNoSisacSchema = z.object({
  /** O número do atendimento no SISAC (ex.: `000777.02`). */
  movimento: z.string().nullable(),
  /** Instante ISO 8601, como o SISAC o guarda (sem fuso). */
  data_hora: z.string().nullable(),
  situacao: z.string().nullable(),
  tipo: z.string().nullable(),
  medico: z.string().nullable(),
  /** O que foi feito (ex.: "RAIO X - COLUNA LOMBO-SACRA"). Dado de saúde. */
  procedimentos: z.string().nullable(),
  convenio: z.string().nullable(),
  plano: z.string().nullable(),
  /** A clínica do grupo onde foi o atendimento. */
  clinica: z.string().nullable(),
  local_origem: z.string().nullable(),
});

export const UltimasVezesDoPacienteSchema = z.object({
  /** Do mais novo ao mais antigo. */
  vezes: z.array(VezNoSisacSchema),
  /** A operação devolveu mais linhas do que o limite dela. */
  cortada: z.boolean(),
});
export type UltimasVezesDoPaciente = z.infer<typeof UltimasVezesDoPacienteSchema>;

/**
 * A regra da busca pelo nome (ADR-0028, item 10), num lugar só para a API e a
 * tela: nome e sobrenome são ao menos duas palavras de duas letras, fora as
 * preposições ("maria da" não é nome e sobrenome).
 */
const PREPOSICOES: ReadonlySet<string> = new Set(['da', 'de', 'do', 'das', 'dos', 'e']);

export function temNomeESobrenome(texto: string): boolean {
  return (
    texto
      .trim()
      .split(/\s+/)
      .filter((palavra) => /\p{L}{2,}/u.test(palavra) && !PREPOSICOES.has(palavra.toLowerCase()))
      .length >= 2
  );
}

/** O mínimo de dígitos do código do paciente na busca, contado sem a pontuação. */
export const MINIMO_DO_CODIGO_DO_PACIENTE = 3;

/** Por onde o paciente foi achado: o CPF digitado, o nome ou código digitado, o telefone do contato. */
const CaminhoDaBuscaSchema = z.enum(['cpf', 'termo', 'telefone']);

/**
 * Procura o paciente no SISAC pelo CPF digitado, pelo nome, sobrenome ou código
 * digitado, e pelo telefone principal do contato (lido no servidor, nunca
 * mandado pela tela).
 */
export const BuscarPacienteDoClienteSchema = z
  .object({
    /** Opcional: sem CPF, procura pelo termo e pelo telefone do contato. */
    cpf: CpfSchema.nullable().optional(),
    /**
     * Nome e sobrenome, ou o código (só dígitos), como a recepção digita. Só
     * quando a organização ligou `buscar_paciente_por_termo`, e só para quem tem
     * a marca do prontuário; nome sem sobrenome responde 400.
     */
    termo: z
      .string()
      .trim()
      .min(3, 'Digite ao menos 3 letras ou números.')
      .max(80)
      .nullable()
      .optional(),
  })
  .strict();
export type BuscarPacienteDoCliente = z.infer<typeof BuscarPacienteDoClienteSchema>;

export const CandidatosAPacienteSchema = z.object({
  candidatos: z.array(
    PacienteNoSisacSchema.extend({
      achado_por: z.array(CaminhoDaBuscaSchema).min(1),
      /** Já vinculado a outro contato desta organização: não dá para vincular aqui. */
      vinculado_a_outro: z.boolean(),
    }),
  ),
  /** Por onde a busca procurou (o contato sem telefone não procura por ele). */
  buscou_por: z.array(CaminhoDaBuscaSchema),
  /**
   * Alguma busca achou mais pacientes do que a resposta leva: a pelo nome ou
   * código corta em 10; as outras, no limite da operação da clínica.
   */
  cortada: z.boolean(),
});
export type CandidatosAPaciente = z.infer<typeof CandidatosAPacienteSchema>;

export const VincularPacienteDoClienteSchema = z
  .object({ codigo: CodigoDoPacienteSchema })
  .strict();
export type VincularPacienteDoCliente = z.infer<typeof VincularPacienteDoClienteSchema>;

/** Cadastra o paciente no SISAC e já vincula o contato a ele. */
export const CadastrarPacienteDoClienteSchema = z
  .object({
    nome: z.string().trim().min(3).max(120),
    cpf: CpfSchema,
    /** Como o SISAC grava: F ou M. */
    sexo: z.enum(['F', 'M']),
    /** `AAAA-MM-DD`. */
    data_nasc: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data no formato AAAA-MM-DD.'),
    /** DDD e número, só dígitos. */
    celular: z.string().regex(/^\d{10,11}$/, 'Celular com DDD, só dígitos.'),
    email: z.string().trim().email().max(120).nullable(),
    /**
     * O SISAC aceita dois pacientes com o mesmo CPF (pergunta "Encontrado outro
     * paciente com o mesmo CPF. Continuar?"). Sem a confirmação, CPF que já tem
     * paciente responde 409 com o erro de campo `cpf: ja_cadastrado`; com ela,
     * cadastra o novo e vincula o novo, nunca o que já existia.
     */
    confirmar_cpf_repetido: z.boolean().optional(),
  })
  .strict();
export type CadastrarPacienteDoCliente = z.infer<typeof CadastrarPacienteDoClienteSchema>;
